import { Link } from 'react-router-dom';
import type { ClientDetail } from '../types';
import { KpiCard } from './KpiCard';
import { DuplicateBadge } from './DuplicateBadge';

function InfoRow({
  label,
  value,
  valueClassName,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly valueClassName?: string;
}) {
  return (
    <div className="flex flex-col">
      <span className="label">{label}</span>
      <span className={valueClassName ?? 'text-[13px] t-strong font-medium break-words'}>{value || '-'}</span>
    </div>
  );
}

/** Full read-only view of a client: identity, fleet KPIs and their equipment list. */
export function ClientView({ client }: { readonly client: ClientDetail }) {
  const envPill = client.env === 'prod' ? 'pill-env-prod' : 'pill-env-staging';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h2 className="page-title">{client.displayName}</h2>
          <span className={envPill}>
            <span className="pill-dot" />
            {client.env}
          </span>
        </div>
        <span className="pill pill-gray">
          {client.status || 'statut inconnu'}
        </span>
      </div>

      {/* KPIs */}
      <div className="stat-strip sm:grid-cols-4">
        <KpiCard label="Equipements" value={client.deviceCount} />
        <KpiCard label="En ligne" value={client.onlineCount} hint="etat MQTT temps reel" />
        <KpiCard label="Valeur flotte" value={`${client.fleetValue.toFixed(2)} EUR`} />
        <KpiCard label="Client depuis" value={client.createdAt ? client.createdAt.slice(0, 10) : '-'} />
      </div>

      {/* Client info */}
      <div className="panel">
        <div className="panel-header">
          <h3 className="panel-title">Informations client</h3>
        </div>
        <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <InfoRow label="Nom complet" value={client.fullName} />
          <InfoRow label="Nom affiche" value={client.name} />
          <InfoRow label="Identifiant" value={client.username} />
          <InfoRow label="Email" value={client.email} />
          <InfoRow label="Telephone" value={client.phone} />
          <InfoRow label="Adresse" value={client.address} />
          <InfoRow label="Statut" value={client.status} />
          <InfoRow label="Cree le" value={client.createdAt} />
          <InfoRow label="UUID" value={client.clientUuid} valueClassName="kbd-id break-all self-start" />
        </div>
      </div>

      {/* Devices */}
      <div className="panel">
        <div className="panel-header">
          <h3 className="panel-title">Equipements ({client.devices.length})</h3>
        </div>
        <div className="table-wrap">
          <table className="w-full">
            <thead>
              <tr>
                <th className="th">Nom</th>
                <th className="th">Type</th>
                <th className="th">N. serie</th>
                <th className="th">HW</th>
                <th className="th">FW</th>
                <th className="th">Station</th>
                <th className="th text-right">Prix</th>
                <th className="th">Statut</th>
              </tr>
            </thead>
            <tbody>
              {client.devices.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state">
                      <span className="text-sm font-medium t-body">Aucun equipement.</span>
                    </div>
                  </td>
                </tr>
              )}
              {client.devices.map((d) => {
                const to = d.deviceSn ? `/devices/${encodeURIComponent(d.deviceSn)}?env=${d.env}` : null;
                return (
                  <tr key={`${d.env}-${d.deviceId}`} className="tr-hover">
                    <td className="td font-medium t-strong">
                      <span className="inline-flex items-center gap-2">
                        {to ? (
                          <Link to={to} className="t-link hover:underline">
                            {d.deviceName || d.deviceSn}
                          </Link>
                        ) : (
                          d.deviceName || '-'
                        )}
                        <DuplicateBadge device={d} />
                      </span>
                    </td>
                    <td className="td">{d.deviceType || '-'}</td>
                    <td className="td">{d.deviceSn || '-'}</td>
                    <td className="td">{d.hardwareVersion || '-'}</td>
                    <td className="td">{d.firmwareVersion || '-'}</td>
                    <td className="td">{d.placeName || d.placeAddress || '-'}</td>
                    <td className="td num text-right">{d.devicePrice != null ? `${d.devicePrice.toFixed(2)} EUR` : '-'}</td>
                    <td className="td">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="pill pill-gray">
                          <span className="pill-dot" />
                          {d.deviceStatus || '-'}
                        </span>
                        {d.online === 1 && (
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]" title="En ligne (MQTT ou OCPP)" />
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
